import { state, log, DEFAULT_SETTINGS, applyTheme } from './ui.js';
import { api } from './api.js';

export const SETTINGS_STORAGE_KEY = 'webapp.courierSettings.v1';

export function cloneDefaultSettings() {
  return { ...DEFAULT_SETTINGS };
}

export function normalizeSettings(raw, fallback) {
  const base = {
    ...cloneDefaultSettings(),
    ...(fallback && typeof fallback === 'object' ? fallback : {}),
  };
  const source = raw && typeof raw === 'object' ? raw : {};
  if (Object.prototype.hasOwnProperty.call(source, 'theme')) {
    const theme = String(source.theme).toLowerCase();
    base.theme = theme === 'light' ? 'light' : 'dark';
  } else {
    base.theme = base.theme === 'light' ? 'light' : 'dark';
  }
  if (Object.prototype.hasOwnProperty.call(source, 'tsdMode')) {
    base.tsdMode = Boolean(source.tsdMode);
  }
  if (Object.prototype.hasOwnProperty.call(source, 'autoOpenCell')) {
    base.autoOpenCell = Boolean(source.autoOpenCell);
  }
  if (Object.prototype.hasOwnProperty.call(source, 'trainingMode')) {
    base.trainingMode = Boolean(source.trainingMode);
  }
  if (Object.prototype.hasOwnProperty.call(source, 'version')) {
    const ver = Number(source.version);
    if (Number.isFinite(ver) && ver >= 1) {
      base.version = Math.floor(ver);
    }
  }
  return base;
}

export function readLocalSettings() {
  try {
    const raw = localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return normalizeSettings(parsed);
  } catch (e) {
    log('settings:local_parse_error ' + (e?.message || e));
    return null;
  }
}

export function writeLocalSettings(settings) {
  try {
    localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  } catch (e) {
    log('settings:local_save_error ' + (e?.message || e));
  }
}

function applySettings(settings, { persistLocal = true, fallback } = {}) {
  const normalized = normalizeSettings(settings, fallback ?? state.settings);
  state.settings = normalized;
  applyTheme(normalized.theme, { persistLocal });
  if (persistLocal) writeLocalSettings(normalized);
  try {
    window.dispatchEvent(new CustomEvent('webapp:settings-applied', { detail: normalized }));
  } catch (_) {}
  return normalized;
}

export function getCourierSettings() {
  return state.settings || cloneDefaultSettings();
}

export async function loadCourierSettings({ silent = false } = {}) {
  void silent;
  const local = readLocalSettings();
  if (local) {
    applySettings(local, { persistLocal: false, fallback: state.settings });
  } else {
    applySettings(DEFAULT_SETTINGS, { persistLocal: false, fallback: state.settings });
  }
  try {
    const remote = await api('/settings', { noLoader: true });
    return applySettings(remote, { fallback: state.settings });
  } catch (e) {
    log('settings:load_failed ' + (e?.message || e));
    if (!local) writeLocalSettings(state.settings);
    return state.settings || cloneDefaultSettings();
  }
}

export async function saveCourierSettings(partial = {}) {
  const current = state.settings || cloneDefaultSettings();
  const merged = normalizeSettings({ ...current, ...partial }, current);
  const baseVersion = Number.isFinite(current.version) ? Number(current.version) : DEFAULT_SETTINGS.version;
  const nextVersion = baseVersion + 1;
  const payload = { version: nextVersion };
  if (Object.prototype.hasOwnProperty.call(partial, 'theme')) payload.theme = merged.theme;
  if (Object.prototype.hasOwnProperty.call(partial, 'tsdMode')) payload.tsdMode = merged.tsdMode;
  if (Object.prototype.hasOwnProperty.call(partial, 'autoOpenCell')) payload.autoOpenCell = merged.autoOpenCell;
  if (Object.prototype.hasOwnProperty.call(partial, 'trainingMode')) payload.trainingMode = merged.trainingMode;
  merged.version = nextVersion;
  applySettings(merged);
  try {
    const remote = await api('/settings', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      noLoader: true,
    });
    return applySettings(remote);
  } catch (e) {
    log('settings:save_failed ' + (e?.message || e));
    merged.version = nextVersion;
    return applySettings(merged);
  }
}
